import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener,
         Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { intersect } from '../../util/array';
import { compareByLabel, compareByValue, partialMatches } from '../../util/compare';
import { Subscription } from '../../util/rxjs';

import { LocaleService } from '../../locale/locale.service';
import { LocalizedModel } from '../../locale/localized-model';
import { LocalizedModelService } from '../../locale/localized-model.service';

export interface Option<T> {
  value: T;
  label: string;
}

@Component({
  selector: 'cp-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.less']
})
export class SelectComponent<T = string | number> implements OnChanges, OnDestroy, OnInit {
  @Input()
  multiple?: boolean;

  @Input()
  label: string;

  @Input()
  large = false;

  @Input()
  placeholder: string;

  @Input()
  emptyPlaceholder: string;

  @Input()
  inputId: string;

  @Input()
  cpFormControl?: UntypedFormControl;

  @Input()
  showErrors = true;

  @Input()
  clearable = false;

  @Input()
  validate = true;

  @Input()
  options: Array<Option<T> | string | number>;

  @Input()
  sortByLabel = true;

  @Input()
  autoSelect: number | undefined;

  @Output()
  selectionChange = new EventEmitter<Option<T> | Array<Option<T>>>();

  @ViewChild('mainInput')
  mainInput: ElementRef;

  isValid = false;
  isOpen = false;

  focusedIndex = 0;
  localizedModel: LocalizedModel<Option<T>>;
  selection: Array<Option<T>> = [];

  private insideSelect = false;
  private valueSubscription: Subscription;
  private filterValue: string | undefined;

  @HostListener('keydown', ['$event'])
  handleKeyDown($event: KeyboardEvent) {
    if (!this.isOpen) {
      this.handleClosedKeypress($event);
    } else {
      this.handleOpenKeypress($event);
    }
  }

  get selectedOption(): Option<T> | undefined {
    if (this.multiple) {
      throw new Error('The single selection accessor should not be used with the multiple select mode');
    }
    return this.selection[0];
  }

  get data() {
    return this.localizedModel.data!;
  }

  get hasSelection() {
    return this.selection.length > 0;
  }

  get stringValue() {
    return this.selection.map(s => s.label).join();
  }

  get filter() {
    return this.filterValue;
  }

  set filter(value: string | undefined) {
    this.filterValue = value;

    const currentFocused = this.focusedIndex && this.data[this.focusedIndex];
    this.localizedModel.onUpdateExisting();

    if (currentFocused) {
      const newIndex = this.data.findIndex(item => item.value === currentFocused.value);
      this.focusedIndex = newIndex === -1 ? 0 : newIndex;
    }
  }

  constructor(private changeDetector: ChangeDetectorRef, localeService: LocaleService,
              localizedModelService: LocalizedModelService) {
    this.localizedModel = new LocalizedModel<Option<T>>(localeService, localizedModelService, {
      filter: value => this.selectFilterFunction(value),
      exclude: value => this.multiple ? this.selection.findIndex(v => v.value === value.value) !== -1 : false,
      sort: data => this.sortByLabel ? data.sort(compareByLabel) : data.sort(compareByValue)
    });
  }

  ngOnInit() {
    this.placeholder = this.placeholder || '';
    this.inputId = this.inputId || '';
    this.cpFormControl = this.cpFormControl || new UntypedFormControl();
    this.valueSubscription = this.cpFormControl.valueChanges
      .subscribe(() => this.isValid = this.cpFormControl!.valid);
  }

  ngOnDestroy() {
    if (this.valueSubscription) {
      this.valueSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const options = changes['options'];
    if (options) {
      this.updateOptions(options.currentValue);
      this.performAutoSelect();
    }
  }

  identify(_index: number, item: Option<T>) {
    return item.value;
  }

  showDropdown() {
    if (this.isOpen) {
      return;
    }

    this.isOpen = true;

    if (!this.multiple) {
      this.focusedIndex = (this.selectedOption && this.data.findIndex(item => this.selectedOption!.value === item.value)) || 0;
    } else {
      this.focusedIndex = 0;
    }
  }

  hideDropdown(fromBlur = false) {
    if (fromBlur && this.insideSelect && this.mainInput) {
      this.mainInput.nativeElement.focus();
      return;
    }

    this.isOpen = false;
    this.filter = '';
  }

  preventFocusLoss($event: Event) {
    $event.preventDefault();
  }

  isSelected(item: Option<T>) {
    if (this.multiple) {
      return false;
    }
    return this.hasSelection && item.value === this.selection[0].value;
  }

  select(item: Option<T>) {
    if (!item) {
      return;
    }

    if (this.multiple) {
      this.selection = [...this.selection, item];
    } else {
      this.selection = [item];
    }
    this.onSelectionChanged();

    if (this.multiple) {
      this.filter = '';
    } else {
      this.hideDropdown();
    }

    this.localizedModel.onUpdateExisting();
    this.changeDetector.detectChanges();
  }

  ensureDropdownOpen(value: string) {
    if (value) {
      this.isOpen = true;
    }
  }

  remove(item: Option<T>) {
    const index = this.selection.findIndex(selected => selected.value === item.value);
    if (index !== -1) {
      this.selection = [
        ...this.selection.slice(0, index),
        ...this.selection.slice(index + 1)
      ];
      this.onSelectionChanged();
      this.localizedModel.onUpdateExisting();
    }
  }

  focusIndex(index: number) {
    this.focusedIndex = index;
  }

  enter() {
    if (!this.insideSelect) {
      this.insideSelect = true;
    }
  }

  leave() {
    if (this.insideSelect) {
      this.insideSelect = false;
    }
  }

  private handleOpenKeypress($event: KeyboardEvent) {
    switch ($event.key) {
      case 'ArrowUp':
        this.focusedIndex = Math.max(0, this.focusedIndex - 1);
        break;
      case 'ArrowDown':
        this.focusedIndex = Math.min(this.data.length - 1, this.focusedIndex + 1);
        break;
      case 'Enter':
        if (this.focusedIndex != null) {
          this.select(this.data[this.focusedIndex]);
        }
        break;
      case 'Escape':
        this.hideDropdown();
    }
  }

  private handleClosedKeypress($event: KeyboardEvent) {
    switch ($event.key) {
      case 'ArrowDown':
      case 'Enter':
        this.showDropdown();
        break;
    }
  }

  private performAutoSelect() {
    if (!this.autoSelect || !this.localizedModel.rawData || this.localizedModel.rawData.length > this.autoSelect) {
      return;
    }

    this.selection = [...this.localizedModel.rawData];
    this.localizedModel.onUpdateExisting();
    this.selectionChange.emit(this.selection);
  }

  private updateOptions(options?: Array<string | number | Option<T>>) {
    const mappedOptions = options && options.map(option => typeof option === 'object' ? option : {
      value: option,
      label: option.toString()
    } as any);
    this.localizedModel.onUpdate(mappedOptions);
    this.updateSelection();
  }

  private get selectFilterFunction() {
    return (option: Option<T>) => !this.filterValue || partialMatches(option, this.filterValue, {
      ignoreCase: true
    });
  }

  private updateSelection() {
    if (!this.options || !this.selection || this.selection.length === 0) {
      return;
    }

    this.selection = intersect(this.selection, this.localizedModel.rawData!, (a, b) => a.value === b.value);
    this.onSelectionChanged();
  }

  private onSelectionChanged() {
    this.selectionChange.emit(this.selection);
  }
}
